import {
    type AcceptedInvitation,
    acceptedInvitationSchema,
    type CreateInvitationInput,
    type CreatedInvitation,
    createdInvitationSchema,
    type Invitation,
    invitationAcceptPath,
    invitationListSchema,
    invitationPath,
    invitationPreviewPath,
    invitationPreviewSchema,
    invitationResendPath,
    type InvitationPreview,
    invitationsPath,
    type RevokedInvitation,
    revokedInvitationSchema,
} from '@baby-tracker/contracts';
import {
    useMutation,
    useQuery,
    useQueryClient,
    type UseMutationResult,
    type UseQueryResult,
} from '@tanstack/react-query';

import { apiGet, apiRequest } from '../lib/api-client.js';
import { queryKeys } from '../lib/query-keys.js';

export function useInvitations(
    familyId: string | undefined,
    enabled: boolean,
): UseQueryResult<Invitation[]> {
    return useQuery({
        queryKey: queryKeys.invitations(familyId ?? ''),
        enabled: enabled && familyId !== undefined,
        queryFn: ({ signal }) =>
            apiGet(invitationsPath(familyId ?? ''), invitationListSchema, { signal }),
    });
}

export function useCreateInvitation(
    familyId: string,
): UseMutationResult<CreatedInvitation, Error, CreateInvitationInput> {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: CreateInvitationInput) =>
            apiRequest('POST', invitationsPath(familyId), createdInvitationSchema, { body: input }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: queryKeys.invitations(familyId) });
        },
    });
}

export function useResendInvitation(
    familyId: string,
): UseMutationResult<CreatedInvitation, Error, string> {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (invitationId: string) =>
            apiRequest(
                'POST',
                invitationResendPath(familyId, invitationId),
                createdInvitationSchema,
            ),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: queryKeys.invitations(familyId) });
        },
    });
}

export function useRevokeInvitation(
    familyId: string,
): UseMutationResult<RevokedInvitation, Error, string> {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (invitationId: string) =>
            apiRequest('DELETE', invitationPath(familyId, invitationId), revokedInvitationSchema),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: queryKeys.invitations(familyId) });
        },
    });
}

export function useInvitationPreview(token: string): UseQueryResult<InvitationPreview> {
    return useQuery({
        queryKey: ['invitation', token],
        queryFn: ({ signal }) =>
            apiGet(invitationPreviewPath(token), invitationPreviewSchema, { signal }),
    });
}

export function useAcceptInvitation(
    token: string,
): UseMutationResult<AcceptedInvitation, Error, void> {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => apiRequest('POST', invitationAcceptPath(token), acceptedInvitationSchema),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: queryKeys.me });
        },
    });
}
